import { getQuoteErrorMessage, isQuoteNotFoundError } from '@/domain/euler/swap'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import { cn } from '@/ui/utils/style'

export interface SwapErrorDisplayProps {
  /** The primary error to display (typically the first non-null from validationError | simulationError | writeError | waitError | quoteError). */
  error: Error | null | undefined
  /** Whether we're still loading quotes or validating them — hides errors while in progress. */
  isLoading?: boolean
  /** Callback to refetch quotes. When provided, a "Retry" button is shown on "Quote not found" errors. */
  onRefetch?: () => void
  /** Whether a refetch is currently in progress. */
  isRefetching?: boolean
  /**
   * When true, E_AccountLiquidity errors are suppressed (returns null).
   * Set this only in dialogs that render their own UndercollateralizedWarning component.
   * Defaults to false — the error is shown as a standard red error box.
   */
  suppressAccountLiquidity?: boolean
  /**
   * Context-specific message override for simulation failures.
   * When provided, this replaces the generic "Transaction simulation failed" message
   * for errors matching simulation patterns (execution reverted, batchSimulation, etc.).
   * Useful for repay dialogs that can provide actionable collateral-specific guidance.
   */
  simulationErrorMessage?: string
  /** Additional className on the outer wrapper. */
  className?: string
}

/**
 * Unified error display for swap dialogs.
 *
 * Replaces per-dialog inline error rendering across all swap flows:
 *   - EvcMultiplyDialogContent
 *   - EvcSwapCollateralDialogContent
 *   - EvcSwapDebtDialogContent
 *   - EvcRepayWithCollateralDialogContent
 *   - EBorrowRepayWithCollateralView
 *   - PositionAssetSwapDialog / EvcAssetSwapDialogContent
 *
 * Key behavioral differences from the old inline approach:
 *   1. All errors are routed through `getQuoteErrorMessage()` which sanitizes
 *      technical details (RPC messages, hex selectors) into user-friendly text.
 *      Raw `error.message` is never displayed.
 *   2. E_AccountLiquidity errors are shown as a standard red error by default.
 *      Only suppressed when `suppressAccountLiquidity` is explicitly true
 *      (for dialogs with their own UndercollateralizedWarning component).
 *   3. Simulation failures show a generic message unless overridden via
 *      `simulationErrorMessage` (used by repay-with-collateral dialogs to
 *      provide collateral-specific guidance).
 *   4. "Quote not found" is displayed as a yellow warning with a Retry button,
 *      instead of being hidden or shown as a red error.
 *
 * Display rules:
 * - "Quote not found" errors → **yellow** warning box with optional Retry button
 * - Standard errors (simulation / write / wait) → **red** error box
 * - No error or still loading → renders nothing
 */
export function SwapErrorDisplay({
  error,
  isLoading,
  onRefetch,
  isRefetching,
  suppressAccountLiquidity,
  simulationErrorMessage,
  className,
}: SwapErrorDisplayProps) {
  if (!error || isLoading) return null

  const message = error.message ?? ''

  // Account liquidity — only suppress when the dialog has its own UndercollateralizedWarning.
  // '34373fbc' is the public EVM error selector for E_AccountLiquidity (not sensitive data).
  // All other error paths are routed through getQuoteErrorMessage() which maps technical
  // details to user-friendly messages — no raw error content reaches the UI.
  if (suppressAccountLiquidity && (message.includes('34373fbc') || message.includes('E_AccountLiquidity'))) {
    return null
  }

  // "Quote not found" — show as yellow warning with optional retry
  if (isQuoteNotFoundError(error)) {
    return (
      <div className={cn('rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3', className)}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-yellow-400">Quote not found</p>
          {onRefetch && (
            <button
              type="button"
              onClick={onRefetch}
              disabled={isRefetching}
              className="shrink-0 rounded-md bg-yellow-500/20 px-3 py-1 font-medium text-xs text-yellow-400 transition-colors hover:bg-yellow-500/30 disabled:opacity-50"
            >
              {isRefetching ? 'Retrying...' : 'Retry'}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Standard error — show as red.
  // Prefer sanitized message from getQuoteErrorMessage; fall back to wagmi's
  // shortMessage (already user-friendly) or a generic fallback. Raw error.message
  // is intentionally excluded to avoid leaking internal/RPC details.
  const sanitizedMessage = getQuoteErrorMessage(error)

  // Allow callers to override simulation failure messages with context-specific guidance
  // (e.g. repay dialogs can mention insufficient collateral).
  const isSimulationError =
    sanitizedMessage === 'Transaction simulation failed. Please try again or adjust the amount.'
  const shortMessage = (error as Error & { shortMessage?: string })?.shortMessage
  const displayMessage =
    (isSimulationError && simulationErrorMessage) || sanitizedMessage || shortMessage || 'Transaction failed. Please try again.'

  return <TransactionErrorPanel error={error} displayMessage={displayMessage} className={cn('mt-0', className)} />
}
